import React, { useState, useEffect } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Select from "../../../components/ui/Select"
import Button from "../../../components/ui/Button"
import ReauthModal from "../../../components/ui/ReauthModal"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../../../components/ui/card"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../components/ui/table"
import { Badge } from "../../../components/ui/badge"
import { listMembers, inviteMember, updateMemberRole, removeMember } from "../../../utils/orgHelpers"

const ROLE_OPTIONS = [
  { value: "admin", label: "Administrator" },
  { value: "instructor", label: "Instructor" },
  { value: "reviewer", label: "Reviewer" },
]

const TeamPanel = ({ organization, currentUserUid }) => {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteRole, setInviteRole] = useState("instructor")
  const [isInviting, setIsInviting] = useState(false)
  const [error, setError] = useState("")
  const [pendingRoleChange, setPendingRoleChange] = useState(null)
  const [pendingRemoveId, setPendingRemoveId] = useState(null)

  const loadMembers = async () => {
    setLoading(true)
    const { members: list } = await listMembers(organization?.orgId)
    setMembers(list || [])
    setLoading(false)
  }

  useEffect(() => {
    if (organization?.orgId) loadMembers()
  }, [organization?.orgId])

  const handleInvite = async (e) => {
    e?.preventDefault()
    setError("")
    if (!inviteEmail?.trim()) {
      setError("Enter an email address")
      return
    }
    setIsInviting(true)
    const { error: inviteError } = await inviteMember({
      orgId: organization?.orgId,
      orgName: organization?.name,
      email: inviteEmail,
      role: inviteRole,
      invitedByName: organization?.name,
    })
    setIsInviting(false)
    if (inviteError) {
      setError(inviteError?.message || "Could not send invite")
      return
    }
    setInviteEmail("")
    loadMembers()
  }

  const handleRoleChange = (memberId, newRole) => setPendingRoleChange({ memberId, newRole })

  const confirmRoleChange = async () => {
    await updateMemberRole(organization?.orgId, pendingRoleChange?.memberId, pendingRoleChange?.newRole)
    setPendingRoleChange(null)
    loadMembers()
  }

  const handleRemove = (memberId) => {
    if (!window.confirm("Remove this team member?")) return
    setPendingRemoveId(memberId)
  }

  const confirmRemove = async () => {
    await removeMember(organization?.orgId, pendingRemoveId)
    setPendingRemoveId(null)
    loadMembers()
  }

  const initials = (email) => (email?.[0] || "?").toUpperCase()

  return (
    <div className="space-y-4 md:col-span-4">
      <Card className="dark:bg-transparent">
        <CardHeader>
          <CardTitle>Invite a Team Member</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input type="email" placeholder="colleague@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e?.target?.value)} />
            </div>
            <div className="sm:w-48">
              <Select options={ROLE_OPTIONS} value={inviteRole} onChange={setInviteRole} placeholder="Role" />
            </div>
            <Button type="submit" variant="default" loading={isInviting}>Send Invite</Button>
          </form>
          {error && <p className="text-sm text-destructive mt-2">{error}</p>}
        </CardContent>
      </Card>

      <Card className="p-0 dark:bg-transparent">
        <CardHeader className="pt-5">
          <CardTitle>Team ({members?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-muted-foreground text-sm">Loading team...</div>
          ) : members?.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">No team members yet.</div>
          ) : (
            <Table className="border-t">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">Member</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="pr-5 text-right">Role</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members?.map((member) => (
                  <TableRow key={member?.memberId}>
                    <TableCell className="pl-5">
                      <div className="flex items-center gap-3">
                        <div className="size-8 rounded-full bg-accent/10 text-accent text-xs font-medium flex items-center justify-center shrink-0">
                          {initials(member?.email)}
                        </div>
                        <span className="truncate font-medium">{member?.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={member?.status === "active" ? "success" : "warning"}>
                        {member?.status === "active" ? "Active" : "Invite Pending"}
                      </Badge>
                    </TableCell>
                    <TableCell className="pr-5">
                      {member?.role === "owner" ? (
                        <span className="text-sm text-muted-foreground flex justify-end">Owner</span>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-36">
                            <Select options={ROLE_OPTIONS} value={member?.role} onChange={(value) => handleRoleChange(member?.memberId, value)} />
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => handleRemove(member?.memberId)} iconName="Trash2" iconSize={16} />
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ReauthModal open={!!pendingRoleChange} label="change this team member's role" onConfirm={confirmRoleChange} onClose={() => setPendingRoleChange(null)} />
      <ReauthModal open={!!pendingRemoveId} label="remove this team member" onConfirm={confirmRemove} onClose={() => setPendingRemoveId(null)} />
    </div>
  )
}

export default TeamPanel